import React from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { Shield, Database } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-black to-gray-900 text-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#FFCC00] rounded-full mb-6">
            <Shield className="h-8 w-8 text-black" />
          </div>
          <h1 className="text-5xl font-bold font-montserrat mb-4">
            Privacy <span className="text-[#FFCC00]">Policy</span>
          </h1>
          <p className="text-gray-400 text-lg">Last Updated: January 23, 2025</p>
        </div>

        <Card className="bg-gray-800/50 border-gray-700 p-8 backdrop-blur-sm">
          {/* Introduction */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold font-montserrat mb-4 text-[#FFCC00]">1. Introduction</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              At Mic Check Millionaire (&quot;MCM&quot;, &quot;we&quot;, &quot;us&quot;, &quot;our&quot;), we respect your privacy and are committed to protecting your personal data. This Privacy Policy explains how we collect, use, store, and protect your information when you use our platform.
            </p>
            <p className="text-gray-300 leading-relaxed">
              By using our service, you agree to the collection and use of information in accordance with this policy.
            </p>
          </section>

          <Separator className="bg-gray-700 my-8" />

          {/* Data Collection */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold font-montserrat mb-4 text-[#FFCC00]">2. Information We Collect</h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold mb-3 text-white flex items-center">
                  <Database className="h-5 w-5 mr-2 text-[#FFCC00]" />
                  Personal Information
                </h3>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li><strong>Account Data:</strong> Name, email address, username, password (encrypted)</li>
                  <li><strong>Profile Information:</strong> Bio, avatar image, social media handles (Cash App, Twitch)</li>
                  <li><strong>User Type:</strong> Artist or Reviewer designation</li>
                  <li><strong>Authentication Data:</strong> Google OAuth credentials (if you sign in with Google)</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3 text-white flex items-center">
                  <Database className="h-5 w-5 mr-2 text-[#FFCC00]" />
                  Content & Activity Data
                </h3>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li><strong>Music Uploads:</strong> Audio files, track titles, artist names, genre information</li>
                  <li><strong>Reviews & Feedback:</strong> Ratings, comments, and feedback logs</li>
                  <li><strong>Session Data:</strong> Review session history, queue activity, earnings data</li>
                  <li><strong>Forum Posts:</strong> Community posts, replies, reactions, and likes</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3 text-white flex items-center">
                  <Database className="h-5 w-5 mr-2 text-[#FFCC00]" />
                  Technical & Usage Data
                </h3>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li><strong>Device Information:</strong> IP address, browser type, device type, operating system</li>
                  <li><strong>Usage Analytics:</strong> Pages visited, time spent, clicks, navigation patterns</li>
                  <li><strong>Cookies:</strong> Session cookies, authentication tokens, preference settings</li>
                </ul>
              </div>
            </div>
          </section>

          <Separator className="bg-gray-700 my-8" />

          {/* How We Use Data */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold font-montserrat mb-4 text-[#FFCC00]">3. How We Use Your Information</h2>
            <p className="text-gray-300 leading-relaxed mb-4">We use the information we collect to:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
              <li>Provide, operate, and maintain our platform</li>
              <li>Process your music submissions and reviews</li>
              <li>Facilitate payments between artists and reviewers</li>
              <li>Send you notifications about your account, submissions, or reviews</li>
              <li>Improve and personalize your experience</li>
              <li>Monitor platform usage and detect technical issues</li>
              <li>Prevent fraud, abuse, and violations of our Terms of Service</li>
              <li>Comply with legal obligations and resolve disputes</li>
              <li>Send you marketing emails (with your consent - you can opt out anytime)</li>
            </ul>
          </section>

          <Separator className="bg-gray-700 my-8" />

          {/* Google OAuth */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold font-montserrat mb-4 text-[#FFCC00]">4. Google OAuth Authentication</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              When you sign in with Google, we collect basic profile information (name, email, profile picture) through Google&apos;s OAuth service. We do not have access to your Google password.
            </p>
            <p className="text-gray-300 leading-relaxed">
              You can revoke MCM&apos;s access to your Google account at any time through your Google Account settings.
            </p>
          </section>

          <Separator className="bg-gray-700 my-8" />

          {/* Data Storage */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold font-montserrat mb-4 text-[#FFCC00]">5. Data Storage & Security</h2>
            <div className="space-y-4 text-gray-300">
              <p className="leading-relaxed">
                Your data is stored securely in MongoDB databases hosted on secure cloud infrastructure. We also use Airtable for administrative data management and analytics.
              </p>
              <p className="leading-relaxed">
                <strong>Security Measures:</strong>
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Passwords are encrypted using industry-standard bcrypt hashing</li>
                <li>All data transmission is secured with HTTPS/SSL encryption</li>
                <li>Access to databases is restricted to authorized personnel only</li>
                <li>Regular security audits and updates</li>
              </ul>
              <p className="leading-relaxed mt-4">
                While we implement strong security measures, no method of transmission or storage is 100% secure. We cannot guarantee absolute security of your data.
              </p>
            </div>
          </section>

          <Separator className="bg-gray-700 my-8" />

          {/* Email Usage */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold font-montserrat mb-4 text-[#FFCC00]">6. Email Communications</h2>
            <p className="text-gray-300 leading-relaxed mb-4">We may send you emails for:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
              <li><strong>Transactional Emails:</strong> Account verification, password resets, submission confirmations</li>
              <li><strong>Notification Emails:</strong> Review completion alerts, queue updates, new forum replies</li>
              <li><strong>Payout Alerts:</strong> Earnings notifications, payment reminders for reviewers</li>
              <li><strong>Marketing Emails:</strong> Platform updates, new features, promotions (opt-in only)</li>
            </ul>
            <p className="text-gray-300 leading-relaxed mt-4">
              You can unsubscribe from marketing emails at any time by clicking the &quot;unsubscribe&quot; link at the bottom of any email.
            </p>
          </section>

          <Separator className="bg-gray-700 my-8" />

          {/* Data Retention */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold font-montserrat mb-4 text-[#FFCC00]">7. Data Retention Policy</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              We retain your personal information for as long as your account is active or as needed to provide you services. You can request deletion of your account and data at any time.
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
              <li><strong>Account Data:</strong> Retained until account deletion</li>
              <li><strong>Music Submissions:</strong> Retained for 12 months after submission date</li>
              <li><strong>Financial Records:</strong> Retained for 7 years for tax and legal compliance</li>
              <li><strong>Analytics Data:</strong> Anonymized and aggregated after 24 months</li>
            </ul>
          </section>

          <Separator className="bg-gray-700 my-8" />

          {/* GDPR & CCPA */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold font-montserrat mb-4 text-[#FFCC00]">8. Your Privacy Rights (GDPR & CCPA)</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              Depending on your location, you may have the following rights:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
              <li><strong>Right to Access:</strong> Request a copy of your personal data</li>
              <li><strong>Right to Rectification:</strong> Correct inaccurate or incomplete data</li>
              <li><strong>Right to Erasure:</strong> Request deletion of your personal data</li>
              <li><strong>Right to Data Portability:</strong> Export your data in a machine-readable format</li>
              <li><strong>Right to Object:</strong> Opt out of certain data processing activities</li>
              <li><strong>Right to Withdraw Consent:</strong> Withdraw consent for data processing</li>
            </ul>
            <p className="text-gray-300 leading-relaxed mt-4">
              To exercise these rights, contact us at <a href="mailto:privacy@miccheckmillionaire.com" className="text-[#FFCC00] hover:underline">privacy@miccheckmillionaire.com</a>
            </p>
          </section>

          <Separator className="bg-gray-700 my-8" />

          {/* Children's Privacy */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold font-montserrat mb-4 text-[#FFCC00]">9. Children&apos;s Privacy</h2>
            <div className="bg-red-900/20 border border-red-700 rounded-lg p-4">
              <p className="text-gray-300 leading-relaxed">
                <strong>Age Requirement:</strong> You must be at least 13 years old to use Mic Check Millionaire. If you are under 18, you must have parental consent. We do not knowingly collect data from children under 13. If we discover that a child under 13 has provided us with personal information, we will delete it immediately.
              </p>
            </div>
          </section>

          <Separator className="bg-gray-700 my-8" />

          {/* Third-Party Services */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold font-montserrat mb-4 text-[#FFCC00]">10. Third-Party Services</h2>
            <p className="text-gray-300 leading-relaxed mb-4">We use the following third-party services:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
              <li><strong>Google OAuth:</strong> For authentication (Google&apos;s privacy policy applies)</li>
              <li><strong>Twitch:</strong> For live streaming integration (Twitch&apos;s privacy policy applies)</li>
              <li><strong>Cash App:</strong> For tip payments (Cash App&apos;s privacy policy applies)</li>
              <li><strong>Airtable:</strong> For data management and analytics</li>
            </ul>
            <p className="text-gray-300 leading-relaxed mt-4">
              These services have their own privacy policies. We are not responsible for their data practices.
            </p>
          </section>

          <Separator className="bg-gray-700 my-8" />

          {/* Changes to Policy */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold font-montserrat mb-4 text-[#FFCC00]">11. Changes to This Policy</h2>
            <p className="text-gray-300 leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new policy on this page with an updated &quot;Last Updated&quot; date. You are advised to review this policy periodically.
            </p>
          </section>

          <Separator className="bg-gray-700 my-8" />

          {/* Contact */}
          <section>
            <h2 className="text-2xl font-bold font-montserrat mb-4 text-[#FFCC00]">12. Contact Us</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              If you have questions about this Privacy Policy or your data, please contact us:
            </p>
            <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-4">
              <p className="text-white">Email: <a href="mailto:privacy@miccheckmillionaire.com" className="text-[#FFCC00] hover:underline">privacy@miccheckmillionaire.com</a></p>
              <p className="text-white mt-2">General Inquiries: <a href="mailto:hello@miccheckmillionaire.com" className="text-[#FFCC00] hover:underline">hello@miccheckmillionaire.com</a></p>
              <p className="text-white mt-2">Support: <Link to="/support" className="text-[#FFCC00] hover:underline">Contact Support Form</Link></p>
            </div>
          </section>
        </Card>

        {/* Footer Links */}
        <div className="mt-12 text-center space-x-6">
          <Link to="/terms" className="text-[#FFCC00] hover:underline">Terms of Service</Link>
          <Link to="/conduct" className="text-[#FFCC00] hover:underline">Code of Conduct</Link>
          <Link to="/support" className="text-[#FFCC00] hover:underline">Contact Support</Link>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;